import React, { useState, useEffect } from 'react';
import { getVersements, createVersement, updateVersement, deleteVersement, getParametrage, getProjets, searchMembresForParcelle, getChargements } from '../services/api';
import { FiPlus, FiEdit2, FiTrash2, FiSearch, FiX, FiChevronUp, FiChevronDown, FiUser } from 'react-icons/fi';

const formatMontant = (n: number) => new Intl.NumberFormat('fr-FR').format(n) + ' FCFA';

const Versements = () => {
  const [versements, setVersements] = useState<any[]>([]);
  const [total, setTotal] = useState(0);
  const [totalMontant, setTotalMontant] = useState(0);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [search, setSearch] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [editingId, setEditingId] = useState('');
  const [typepaiements, setTypepaiements] = useState<any[]>([]);
  const [projets, setProjets] = useState<any[]>([]);
  const [chargementsFilter, setChargementsFilter] = useState<any[]>([]);
  const [success, setSuccess] = useState('');
  const [error, setError] = useState('');
  const [form, setForm] = useState({ user_id: '', dateversement: '', montant: '', typepaiement_id: '', projet_id: '', reference: '', description: '' });

  // Autocomplete membre
  const [membreSearch, setMembreSearch] = useState('');
  const [membreResults, setMembreResults] = useState<any[]>([]);
  const [selectedMembre, setSelectedMembre] = useState<any>(null);
  const [showMembreDropdown, setShowMembreDropdown] = useState(false);
  const [membresLoading, setMembresLoading] = useState(false);
  const searchTimeout = React.useRef<any>(null);

  // Filters
  const [filterType, setFilterType] = useState('');
  const [filterProjet, setFilterProjet] = useState('');
  const [filterChargement, setFilterChargement] = useState('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');

  // Sort
  const [sortBy, setSortBy] = useState('dateversement');
  const [sortOrder, setSortOrder] = useState<'ASC' | 'DESC'>('DESC');

  const loadData = () => {
    const params: any = { page, limit: 50, q: search, sort_by: sortBy, sort_order: sortOrder };
    if (filterType) params.typepaiement_id = filterType;
    if (filterProjet) params.projet_id = filterProjet;
    if (filterChargement) params.chargement_id = filterChargement;
    if (dateFrom) params.date_from = dateFrom;
    if (dateTo) params.date_to = dateTo;

    getVersements(params).then(r => {
      setVersements(r.data.data);
      setTotal(r.data.total);
      setTotalMontant(r.data.totalMontant || 0);
      setTotalPages(r.data.totalPages);
    }).catch(() => {});
  };

  useEffect(() => { loadData(); }, [page, search, filterType, filterProjet, filterChargement, dateFrom, dateTo, sortBy, sortOrder]);
  useEffect(() => {
    getParametrage('typepaiements').then(r => setTypepaiements(r.data)).catch(() => {});
    getProjets().then(r => setProjets(r.data)).catch(() => {});
    getChargements().then(r => setChargementsFilter(r.data)).catch(() => {});
  }, []);

  const resetForm = () => {
    setForm({ user_id: '', dateversement: '', montant: '', typepaiement_id: '', projet_id: '', reference: '', description: '' });
    setMembreSearch('');
    setSelectedMembre(null);
    setMembreResults([]);
    setShowMembreDropdown(false);
  };

  const openAdd = () => { resetForm(); setEditingId(''); setShowModal(true); };
  const openEdit = (v: any) => {
    setForm({ user_id: v.user_id || '', dateversement: v.dateversement ? v.dateversement.substring(0, 10) : '', montant: v.montant || '', typepaiement_id: v.typepaiement_id || '', projet_id: v.projet_id || '', reference: v.reference || '', description: v.description || '' });
    // Pré-remplir le membre sélectionné
    if (v.user_id && (v.membre_nom || v.prenom || v.nom)) {
      const membreNom = v.membre_nom || `${v.prenom || ''} ${v.nom || ''}`.trim();
      setSelectedMembre({ id: v.user_id, prenomnom: membreNom, matricule: v.membre_matricule || v.matricule || '' });
      setMembreSearch(membreNom);
    } else {
      setSelectedMembre(null);
      setMembreSearch('');
    }
    setMembreResults([]);
    setShowMembreDropdown(false);
    setEditingId(v.id);
    setShowModal(true);
  };

  const handleMembreSearchChange = (val: string) => {
    setMembreSearch(val);
    if (selectedMembre) {
      setSelectedMembre(null);
      setForm(f => ({ ...f, user_id: '' }));
    }
    if (searchTimeout.current) clearTimeout(searchTimeout.current);
    if (val.trim().length < 2) {
      setMembreResults([]);
      setShowMembreDropdown(false);
      return;
    }
    searchTimeout.current = setTimeout(() => {
      setMembresLoading(true);
      searchMembresForParcelle(val.trim())
        .then(r => {
          setMembreResults(Array.isArray(r.data) ? r.data : []);
          setShowMembreDropdown(true);
        })
        .catch(() => setMembreResults([]))
        .finally(() => setMembresLoading(false));
    }, 300);
  };

  const selectMembre = (m: any) => {
    setSelectedMembre(m);
    setMembreSearch(m.prenomnom);
    setForm(f => ({ ...f, user_id: m.id }));
    setShowMembreDropdown(false);
  };

  const handleSave = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      const payload = {
        ...form,
        montant: form.montant ? Number(form.montant) : null,
        matricule: selectedMembre?.matricule || '',
        prenom: selectedMembre?.prenomnom?.split(' ')[0] || '',
        nom: selectedMembre?.prenomnom?.split(' ').slice(1).join(' ') || ''
      };
      if (editingId) {
        await updateVersement(editingId, payload);
        setSuccess('Versement mis à jour');
      } else {
        await createVersement(payload);
        setSuccess('Versement créé avec succès');
      }
      setShowModal(false);
      loadData();
      setTimeout(() => setSuccess(''), 3000);
    } catch (err: any) {
      setError(err.response?.data?.error || 'Erreur');
      setTimeout(() => setError(''), 3000);
    }
  };

  const handleDelete = async (id: string) => {
    if (!window.confirm('Supprimer ce versement ?')) return;
    await deleteVersement(id);
    loadData();
  };

  const handleSort = (col: string) => {
    if (sortBy === col) {
      setSortOrder(sortOrder === 'ASC' ? 'DESC' : 'ASC');
    } else {
      setSortBy(col);
      setSortOrder(col === 'montant' ? 'DESC' : 'ASC');
    }
    setPage(1);
  };

  const SortIcon = ({ col }: { col: string }) => {
    if (sortBy !== col) return <span className="sort-icon inactive"><FiChevronUp style={{ opacity: 0.3, fontSize: 12 }} /></span>;
    return <span className="sort-icon active">{sortOrder === 'ASC' ? <FiChevronUp /> : <FiChevronDown />}</span>;
  };

  const clearFilters = () => {
    setSearch('');
    setFilterType('');
    setFilterProjet('');
    setFilterChargement('');
    setDateFrom('');
    setDateTo('');
    setSortBy('dateversement');
    setSortOrder('DESC');
    setPage(1);
  };

  const hasFilters = search || filterType || filterProjet || filterChargement || dateFrom || dateTo;

  return (
    <div>
      <h1 className="page-title">Liste des versements</h1>

      {success && <div className="alert alert-success">{success}</div>}
      {error && <div className="alert alert-error">{error}</div>}

      <div className="kpi-row" style={{ display: 'flex', gap: '1rem', marginBottom: '1.5rem' }}>
        <div className="card" style={{ flex: 1 }}>
          <div className="card-body" style={{ textAlign: 'center' }}>
            <div style={{ fontSize: '0.85rem', color: '#666', marginBottom: '0.25rem' }}>Nombre de versements</div>
            <div style={{ fontSize: '1.5rem', fontWeight: 700 }}>{total.toLocaleString('fr-FR')}</div>
          </div>
        </div>
        <div className="card" style={{ flex: 1 }}>
          <div className="card-body" style={{ textAlign: 'center' }}>
            <div style={{ fontSize: '0.85rem', color: '#666', marginBottom: '0.25rem' }}>Total montant</div>
            <div style={{ fontSize: '1.5rem', fontWeight: 700 }}>{formatMontant(totalMontant)}</div>
          </div>
        </div>
      </div>

      <div className="card">
        <div className="card-header">
          <span>Versements enregistrés ({total.toLocaleString('fr-FR')})</span>
          <button className="btn btn-success" onClick={openAdd}><FiPlus /> Nouveau versement</button>
        </div>
        <div className="card-body">
          {/* Filters toolbar - all on same line */}
          <div className="filters-bar">
            <div className="search-box" style={{ minWidth: 180 }}>
              <FiSearch className="search-icon" />
              <input type="text" className="form-input" placeholder="Rechercher..." value={search} onChange={e => { setSearch(e.target.value); setPage(1); }} />
            </div>
            <select className="form-input filter-select" value={filterType} onChange={e => { setFilterType(e.target.value); setPage(1); }}>
              <option value="">Tous les types</option>
              {typepaiements.map((t: any) => <option key={t.id} value={t.id}>{t.libelle}</option>)}
            </select>
            <select className="form-input filter-select" value={filterProjet} onChange={e => { setFilterProjet(e.target.value); setPage(1); }}>
              <option value="">Tous les projets</option>
              {projets.map((pr: any) => <option key={pr.id} value={pr.id}>{pr.libelle || pr.nom}</option>)}
            </select>
            <select className="form-input filter-select" value={filterChargement} onChange={e => { setFilterChargement(e.target.value); setPage(1); }}>
              <option value="">Tous les chargements</option>
              {chargementsFilter.map((c: any) => <option key={c.id} value={c.id}>{c.libelle} ({c.nblignes || 0} lignes)</option>)}
            </select>
            <div className="filter-date-group">
              <input type="date" className="form-input filter-date" value={dateFrom} onChange={e => { setDateFrom(e.target.value); setPage(1); }} title="Date début" placeholder="Du" />
              <span className="filter-date-sep">—</span>
              <input type="date" className="form-input filter-date" value={dateTo} onChange={e => { setDateTo(e.target.value); setPage(1); }} title="Date fin" placeholder="Au" />
            </div>
            {hasFilters && (
              <button className="btn btn-outline btn-sm" onClick={clearFilters} title="Effacer les filtres">
                <FiX /> Effacer
              </button>
            )}
          </div>

          <div className="table-responsive">
            <table className="data-table sortable-table">
              <thead>
                <tr>
                  <th className="sortable-th" onClick={() => handleSort('dateversement')}>Date <SortIcon col="dateversement" /></th>
                  <th className="sortable-th" onClick={() => handleSort('matricule')}>Matricule <SortIcon col="matricule" /></th>
                  <th className="sortable-th" onClick={() => handleSort('prenom')}>Membre <SortIcon col="prenom" /></th>
                  <th className="sortable-th" onClick={() => handleSort('type_libelle')}>Type <SortIcon col="type_libelle" /></th>
                  <th>Projet</th>
                  <th className="sortable-th" onClick={() => handleSort('reference')}>Référence <SortIcon col="reference" /></th>
                  <th className="sortable-th" onClick={() => handleSort('montant')}>Montant <SortIcon col="montant" /></th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {versements.map(v => (
                  <tr key={v.id}>
                    <td>{v.dateversement ? new Date(v.dateversement).toLocaleDateString('fr-FR') : '-'}</td>
                    <td>{v.membre_matricule || v.matricule || '-'}</td>
                    <td>{v.membre_nom || ((v.prenom || v.nom) ? `${v.prenom || ''} ${v.nom || ''}`.trim() : '-')}</td>
                    <td>{v.type_libelle ? <span className="badge badge-blue">{v.type_libelle}</span> : '-'}</td>
                    <td>{v.projet_libelle ? <span className="badge badge-green">{v.projet_libelle}</span> : '-'}</td>
                    <td>{v.reference || '-'}</td>
                    <td style={{ fontWeight: 600 }}>{v.montant ? formatMontant(Number(v.montant)) : '-'}</td>
                    <td>
                      <div className="actions">
                        <button className="btn-icon btn-warning" onClick={() => openEdit(v)}><FiEdit2 /></button>
                        <button className="btn-icon btn-danger" onClick={() => handleDelete(v.id)}><FiTrash2 /></button>
                      </div>
                    </td>
                  </tr>
                ))}
                {versements.length === 0 && (
                  <tr><td colSpan={8} style={{ textAlign: 'center', padding: '2rem', color: '#999' }}>Aucun versement trouvé</td></tr>
                )}
              </tbody>
            </table>
          </div>

          {totalPages > 1 && (
            <div className="pagination">
              {page > 1 && <button onClick={() => setPage(page - 1)}>&laquo;</button>}
              {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                const p = Math.max(1, page - 2) + i;
                if (p > totalPages) return null;
                return <button key={p} className={p === page ? 'active' : ''} onClick={() => setPage(p)}>{p}</button>;
              })}
              {page < totalPages && <button onClick={() => setPage(page + 1)}>&raquo;</button>}
            </div>
          )}
        </div>
      </div>

      {/* Modal */}
      {showModal && (
        <div className="modal-overlay" onClick={() => setShowModal(false)}>
          <div className="modal" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h3>{editingId ? 'Modifier le versement' : 'Nouveau versement'}</h3>
              <button className="modal-close" onClick={() => setShowModal(false)}><FiX /></button>
            </div>
            <form onSubmit={handleSave}>
              <div className="modal-body">
                {/* Autocomplete Membre */}
                <div className="form-group" style={{ position: 'relative' }}>
                  <label>Membre *</label>
                  {selectedMembre ? (
                    <div className="selected-membre-card">
                      <FiUser className="selected-membre-icon" />
                      <div className="selected-membre-info">
                        <strong>{selectedMembre.prenomnom}</strong>
                        <small>{selectedMembre.matricule && `Mat: ${selectedMembre.matricule}`}{selectedMembre.telephone && ` | ${selectedMembre.telephone}`}</small>
                      </div>
                      <button type="button" className="btn-icon" onClick={() => { setSelectedMembre(null); setMembreSearch(''); setForm(f => ({ ...f, user_id: '' })); }} title="Changer de membre"><FiX /></button>
                    </div>
                  ) : (
                    <>
                      <div className="search-box" style={{ marginBottom: 0 }}>
                        <FiSearch className="search-icon" />
                        <input
                          type="text"
                          className="form-input"
                          placeholder="Rechercher un membre (nom, matricule, tél)..."
                          value={membreSearch}
                          onChange={e => handleMembreSearchChange(e.target.value)}
                          autoFocus
                        />
                        {membresLoading && <span className="search-loading">...</span>}
                      </div>
                      {showMembreDropdown && membreResults.length > 0 && (
                        <div className="autocomplete-dropdown">
                          {membreResults.map((m: any) => (
                            <div key={m.id} className="autocomplete-item" onClick={() => selectMembre(m)}>
                              <div className="autocomplete-item-name">{m.prenomnom}</div>
                              <div className="autocomplete-item-detail">
                                {m.matricule && <span>Mat: {m.matricule}</span>}
                                {m.telephone && <span> | {m.telephone}</span>}
                              </div>
                            </div>
                          ))}
                        </div>
                      )}
                      {showMembreDropdown && membreResults.length === 0 && membreSearch.length >= 2 && !membresLoading && (
                        <div className="autocomplete-dropdown">
                          <div className="autocomplete-item-empty">Aucun membre trouvé</div>
                        </div>
                      )}
                    </>
                  )}
                </div>
                <div className="form-row">
                  <div className="form-group"><label>Date versement</label><input type="date" className="form-input" value={form.dateversement} onChange={e => setForm({ ...form, dateversement: e.target.value })} required /></div>
                  <div className="form-group"><label>Montant</label><input type="number" className="form-input" value={form.montant} onChange={e => setForm({ ...form, montant: e.target.value })} required /></div>
                </div>
                <div className="form-row">
                  <div className="form-group"><label>Type de paiement</label>
                    <select className="form-input" value={form.typepaiement_id} onChange={e => setForm({ ...form, typepaiement_id: e.target.value })}>
                      <option value="">--</option>
                      {typepaiements.map((t: any) => <option key={t.id} value={t.id}>{t.libelle}</option>)}
                    </select>
                  </div>
                  <div className="form-group"><label>Projet</label>
                    <select className="form-input" value={form.projet_id} onChange={e => setForm({ ...form, projet_id: e.target.value })}>
                      <option value="">--</option>
                      {projets.map((pr: any) => <option key={pr.id} value={pr.id}>{pr.libelle || pr.nom}</option>)}
                    </select>
                  </div>
                </div>
                <div className="form-group"><label>Référence</label><input className="form-input" value={form.reference} onChange={e => setForm({ ...form, reference: e.target.value })} /></div>
                <div className="form-group"><label>Description</label><textarea className="form-input" value={form.description} onChange={e => setForm({ ...form, description: e.target.value })} rows={3} /></div>
              </div>
              <div className="modal-footer">
                <button type="button" className="btn btn-outline" onClick={() => setShowModal(false)}>Annuler</button>
                <button type="submit" className="btn btn-primary">Enregistrer</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default Versements;
